import { Button, Card, Form, Input, Popconfirm, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationSessionSelect } from '@/pages/education/components/EducationLookupSelects';

type Enrollment = {
  id: string;
  student_id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  route_id: string;
  route_code?: string;
  route_name?: string;
  stop_id: string;
  stop_code?: string;
  stop_name?: string;
  session_id?: string;
  session_name?: string;
  effective_from: string;
  effective_to?: string | null;
  status: string;
};

type RouteRow = { id: string; code: string; name: string };

export default function TransportEnrollmentsPage() {
  const { t } = useTranslation('education');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const studentFilter = searchParams.get('student_id') || undefined;
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.transport.manage') ||
      user?.permissions?.includes('education.admin')
  );
  const canRaiseFees = Boolean(
    canManage &&
      (user?.permissions?.includes('education.fee.demand') ||
        user?.permissions?.includes('education.admin'))
  );

  const [sessionId, setSessionId] = useState<string | undefined>();
  const [routeId, setRouteId] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [feePeriodKey, setFeePeriodKey] = useState('2026-Q1');

  const list = useQuery({
    queryKey: ['education-transport-enrollments', studentFilter, sessionId, routeId, status, q],
    queryFn: () =>
      api.get<Enrollment[]>('/education/transport/enrollments', {
        params: {
          student_id: studentFilter,
          session_id: sessionId,
          route_id: routeId,
          status,
          q: q || undefined,
        },
      }),
  });

  const routes = useQuery({
    queryKey: ['education-transport-routes'],
    queryFn: () => api.get<RouteRow[]>('/education/transport/routes'),
  });

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['education-transport-enrollments'] });

  const endEnrollment = useMutation({
    mutationFn: (id: string) => api.post(`/education/transport/enrollments/${id}/end`, {}),
    onSuccess: () => {
      message.success(t('transportEnrollmentEnded'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const generateFees = useMutation({
    mutationFn: () =>
      api.post('/education/transport/fee-demands/generate', {
        period_key: feePeriodKey,
        session_id: sessionId || undefined,
        route_id: routeId || undefined,
      }),
    onSuccess: (result) => {
      const body = result.data as {
        created?: number;
        reused?: number;
        skipped?: number;
        riders?: number;
      };
      message.success(
        t('transportFeeDemandsGenerated', {
          created: body?.created ?? 0,
          reused: body?.reused ?? 0,
          skipped: body?.skipped ?? 0,
          riders: body?.riders ?? 0,
        })
      );
      queryClient.invalidateQueries({ queryKey: ['education-fee-demands'] });
      queryClient.invalidateQueries({ queryKey: ['education-fee-outstanding'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const newPath = studentFilter
    ? `/education/transport/enrollments/new?student_id=${studentFilter}`
    : '/education/transport/enrollments/new';

  return (
    <EducationPageChrome
      title={t('transportEnrollments')}
      description={t('transportEnrollmentsIntro')}
      extra={
        <Space wrap>
          {canManage ? (
            <>
              <Button type="primary" onClick={() => navigate(newPath)}>
                {t('newTransportEnrollment')}
              </Button>
              <Button onClick={() => navigate('/education/transport/enrollments/bulk')}>
                {t('bulkTransportEnrollment')}
              </Button>
            </>
          ) : null}
          <Button onClick={() => navigate('/education/transport')}>{t('backToTransport')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small">
          <Form layout="inline" onFinish={() => list.refetch()}>
            <Form.Item label={t('session')}>
              <EducationSessionSelect
                allowClear
                style={{ minWidth: 200 }}
                value={sessionId}
                onChange={(v) => setSessionId(v || undefined)}
              />
            </Form.Item>
            <Form.Item label={t('transportRoute')}>
              <Select
                allowClear
                style={{ minWidth: 220 }}
                value={routeId}
                onChange={(v) => setRouteId(v || undefined)}
                options={(routes.data?.data ?? []).map((r) => ({
                  value: r.id,
                  label: `${r.code} — ${r.name}`,
                }))}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
            <Form.Item label={t('status')}>
              <Select
                allowClear
                style={{ minWidth: 140 }}
                value={status}
                onChange={(v) => setStatus(v || undefined)}
                options={[
                  { value: 'active', label: t('active') },
                  { value: 'waitlisted', label: t('waitlisted') },
                  { value: 'ended', label: t('ended') },
                ]}
              />
            </Form.Item>
            <Form.Item label={t('search')}>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                allowClear
                placeholder={t('feeOutstandingSearchHint')}
                style={{ width: 200 }}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={list.isFetching}>
              {t('filter')}
            </Button>
            {studentFilter ? (
              <Button
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('student_id');
                  setSearchParams(next);
                }}
              >
                {t('clearStudentFilter')}
              </Button>
            ) : null}
          </Form>
        </Card>

        {canRaiseFees ? (
          <Card size="small" title={t('raiseTransportFeeDemands')}>
            <Space wrap>
              <Input
                value={feePeriodKey}
                onChange={(e) => setFeePeriodKey(e.target.value)}
                placeholder="2026-Q1"
                style={{ width: 140 }}
                addonBefore={t('periodKey')}
              />
              <Button type="primary" loading={generateFees.isPending} onClick={() => generateFees.mutate()}>
                {t('raiseTransportFeeDemands')}
              </Button>
              <Button onClick={() => navigate('/education/fees/outstanding')}>{t('feeOutstanding')}</Button>
            </Space>
            <div style={{ marginTop: 8, color: 'var(--ant-color-text-secondary)', fontSize: 12 }}>
              {t('raiseTransportFeeDemandsHint')}
            </div>
          </Card>
        ) : null}

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={list.isLoading}
            dataSource={list.data?.data ?? []}
            pagination={{ pageSize: 25 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: Enrollment) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              {
                title: t('transportRoute'),
                render: (_: unknown, row: Enrollment) => `${row.route_code || ''} — ${row.route_name || ''}`,
              },
              {
                title: t('stop'),
                render: (_: unknown, row: Enrollment) => `${row.stop_code || ''} — ${row.stop_name || ''}`,
              },
              { title: t('session'), dataIndex: 'session_name', width: 140 },
              { title: t('effectiveOn'), dataIndex: 'effective_from', width: 120 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 110,
                render: (v: string) => (
                  <Tag color={v === 'active' ? 'success' : v === 'waitlisted' ? 'warning' : undefined}>{v}</Tag>
                ),
              },
              ...(canManage
                ? [
                    {
                      title: t('actions'),
                      width: 200,
                      render: (_: unknown, row: Enrollment) => (
                        <Space>
                          <Button
                            size="small"
                            onClick={() => navigate(`/education/transport/enrollments/${row.id}`)}
                          >
                            {tc('edit')}
                          </Button>
                          {row.status === 'active' || row.status === 'waitlisted' ? (
                            <Popconfirm
                              title={t('transportEnrollmentEndConfirm')}
                              onConfirm={() => endEnrollment.mutate(row.id)}
                            >
                              <Button size="small" loading={endEnrollment.isPending}>
                                {t('endEnrollment')}
                              </Button>
                            </Popconfirm>
                          ) : null}
                        </Space>
                      ),
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
